import type { PlatformPayment } from "@station/contracts";
import { Button, DataTable, EmptyState, FormField, Select } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Wallet } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ReasonActionDialog,
  RecordPaymentDialog,
  reversePayment,
} from "../../features/platform/billing-dialogs.js";
import {
  usePlatformPayments,
  usePlatformPermissions,
  useTenantOptions,
} from "../../features/platform/queries.js";
import { PlatformPage } from "../../features/platform/shared.js";
import { usePaymentColumns } from "../../features/platform/tenant-tabs.js";
import { useCursorPages } from "../../lib/cursor-pages.js";
import { errorMessage } from "../../lib/errors.js";

export const Route = createFileRoute("/platform/tahsilat")({ component: PaymentsRoute });

/**
 * Tahsilat (Prompt 24 web): kiracıdan gelen ödemeler en eski açık faturadan başlayarak eşleşir; artan tutar kiracının
 * alacağı olarak kalır. Hatalı kayıt silinmez — nedenle ters çevrilir (eşleşmeler geri alınır, gecikme yeniden değerlendirilir).
 */
function PaymentsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = usePlatformPermissions();
  const canManage = permissions.has("platform.billing.manage");
  const tenants = useTenantOptions();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [reversing, setReversing] = useState<PlatformPayment | null>(null);
  const pages = useCursorPages(tenantId ?? "");
  const payments = usePlatformPayments(tenantId, pages.cursor);
  const columns = usePaymentColumns(true, canManage ? setReversing : null);
  const options = (tenants.data ?? []).map((tenant) => ({ id: tenant.id, name: tenant.name }));

  return (
    <PlatformPage
      title={t("platform.payments.title")}
      description={t("platform.payments.description")}
      permission="platform.billing.read"
      actions={
        canManage ? (
          <Button onClick={() => setRecording(true)} disabled={options.length === 0}>
            <Plus aria-hidden="true" />
            {t("platform.payments.record")}
          </Button>
        ) : null
      }
    >
      <DataTable
        caption={t("platform.payments.title")}
        columns={columns}
        rows={payments.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={payments.isPending}
        error={payments.error ? errorMessage(payments.error) : undefined}
        emptyState={<EmptyState icon={<Wallet />} title={t("platform.payments.empty")} />}
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: payments.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(payments.data?.page.nextCursor),
        }}
        filters={
          <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label={t("platform.fields.tenant")}>
              {(control) => (
                <Select
                  {...control}
                  value={tenantId ?? ""}
                  onChange={(event) => setTenantId(event.target.value || null)}
                >
                  <option value="">{t("platform.all")}</option>
                  {options.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </div>
        }
      />
      {recording ? (
        <RecordPaymentDialog open onOpenChange={setRecording} tenantId={tenantId} tenantOptions={options} />
      ) : null}
      {reversing ? (
        <ReasonActionDialog
          open
          onOpenChange={(open) => !open && setReversing(null)}
          title={t("platform.payments.reverseTitle")}
          description={t("platform.payments.reverseDescription")}
          submitLabel={t("platform.payments.reverse")}
          successTitle={t("platform.payments.reversedToast")}
          run={(reason) => reversePayment(reversing, reason)}
        />
      ) : null}
    </PlatformPage>
  );
}
